/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useEffect, useRef, useState } from 'react';
import cmsUtils from '../../cms/utils/cmsUtils';
import utils from '../../utils';

const DEFAULT_URL = 'https://coverage-calculator.netlify.app/';
const DEFAULT_HEIGHT = 900;

export function CoverageCalculator(props) {

  const item = props.item;
  const cmsOption = props.cmsOption || {};
  const cssClass = utils.classNames('cms_item', 'coverage__calculator', item.cssClass || item.anchorName || '');

  const url = cmsUtils.payload(item, 'Url') || DEFAULT_URL;
  const fallbackHeight = parseInt(cmsUtils.payload(item, 'Height'), 10) || DEFAULT_HEIGHT;

  const iframeRef = useRef(null);
  const [height, setHeight] = useState(fallbackHeight);

  useEffect(() => {
    if (cmsOption.isCmsEdit) {
      return;
    }

    // 1. Same-origin path: measure the iframe's content directly from here.
    //    Works only when the calculator is served from the same origin as this
    //    site (e.g. hosted under /coverage-calculator/). Throws for cross-origin.
    let cleanupSameOrigin = null;
    const trySameOriginMeasure = () => {
      try {
        const doc = iframeRef.current && iframeRef.current.contentWindow.document;
        if (!doc || !doc.body) return false;

        // Same-origin only: hide the calculator's own logo when embedded here,
        // since the GoProtect site already shows its branding around it.
        if (doc.head && !doc.getElementById('cc-embed-style')) {
          const style = doc.createElement('style');
          style.id = 'cc-embed-style';
          style.textContent = 'img[src$="/logo.png"]{display:none!important;}';
          doc.head.appendChild(style);
        }

        const measure = () => setHeight(doc.body.scrollHeight);
        measure();
        if (typeof ResizeObserver !== 'undefined') {
          const ro = new ResizeObserver(measure);
          ro.observe(doc.body);
          cleanupSameOrigin = () => ro.disconnect();
        }
        return true;
      } catch (e) {
        // Cross-origin — fall through to postMessage below.
        return false;
      }
    };

    const iframeEl = iframeRef.current;
    const onLoad = () => trySameOriginMeasure();
    if (iframeEl) iframeEl.addEventListener('load', onLoad);

    // 2. Cross-origin path: accept height messages posted by the calculator.
    const expectedOrigin = (() => {
      try { return new URL(url, window.location.href).origin; } catch (e) { return null; }
    })();

    const onMessage = (event) => {
      if (expectedOrigin && event.origin !== expectedOrigin) return;
      const data = event.data;
      if (data && data.type === 'cc-height' && typeof data.height === 'number') {
        setHeight(data.height);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      if (iframeEl) iframeEl.removeEventListener('load', onLoad);
      window.removeEventListener('message', onMessage);
      if (cleanupSameOrigin) cleanupSameOrigin();
    };
    // eslint-disable-next-line
  }, []);

  if (cmsOption.isCmsEdit) {
    return (
      <div className={cssClass} data-cms-item-id={item.itemId} id={item.anchorName}>
        <pre style={{ fontSize: '12px', margin: '5px', padding: '10px', background: '#eee', border: '1px solid #ccc' }}>
          <b> Coverage Calculator </b> — {url}
        </pre>
      </div>
    );
  }

  return (
    <div className={cssClass} id={item.anchorName} style={{ maxWidth: '960px', margin: '2rem auto' }}>
      <iframe
        ref={iframeRef}
        title="Insurance Gap Calculator"
        src={url}
        frameBorder="0"
        loading="lazy"
        scrolling="auto"
        width="100%"
        height={height}
        style={{ display: 'block', width: '100%', border: 0 }}
      ></iframe>
    </div>
  );
}
